import type {
  RefineBatchInput,
  RefineBatchResult,
} from "./library-refine-upload";
import type { AiModels } from "./llm-data";
import type { FileRef } from "./library-adapter";
export type ReviewNotice = {
  code: string;
  level: "warn" | "info";
  message: string;
  line?: number;
  data?: { missing?: string[]; added?: string[]; [key: string]: unknown };
};
export type Guideline = {
  id: string;
  kind: "refine" | "generate";
  version: number;
  label: string;
  body: string;
  active: boolean;
  author: string;
  createdAt: string;
  note: string;
  uses: number;
};
export type RefineStatus =
  | "queued"
  | "running"
  | "review"
  | "registered"
  | "rejected"
  | "failed"
  | "cancelled";
export const refineLabels: Record<RefineStatus, string> = {
  queued: "큐 대기",
  running: "정형화 중",
  review: "검수 대기",
  registered: "등록됨",
  rejected: "반려",
  failed: "실패",
  cancelled: "취소",
};
export type RefineAttempt = {
  seq: number;
  sourceRevision: number;
  guideline: { version: number; label: string; id: string };
  model: string;
  provider: "openai" | "codex";
  status: string;
  costUsd: number;
  durationMs: number;
  startedAt?: string;
  decision?: string;
  decisionNote?: string;
  registeredRevision?: number;
  error?: string;
  body: string;
  report: {
    status: string;
    hold: string[];
    conflicts: { line: number; text: string; effect: string }[];
    removed: { text: string; movedTo: string }[];
    unusedFiles: string[];
    unsupportedFiles?: string[];
    auxDocuments?: { name: string; chars?: number; truncated?: boolean }[];
    repeated: { text: string; count: number }[];
    scale: {
      media: number;
      videos: number;
      linkCards: number;
      headings: number;
      paragraphs: number;
      steps?: number | null;
    };
    registration: { keywords: string[]; schedule: string; memo: string };
  };
  notices: ReviewNotice[];
  parse: { errors: string[]; warnings: string[] };
  context: string[];
};
export type RefineJob = {
  id: string;
  title: string;
  customerId: string;
  customer: string;
  status: RefineStatus;
  author: string;
  createdAt: string;
  folderId: string;
  sources: { revision: number; body: string; files: FileRef[] }[];
  attempts: RefineAttempt[];
  manuscriptId?: string;
  linkedManuscript?: { title: string; revision: number };
  /** Server-selected completed attempt eligible for registration. */
  registrationAttemptSeq?: number;
  allowedActions: RefineAction[];
};
export type RefineAction =
  | "register"
  | "reject"
  | "rerun"
  | "source"
  | "cancel"
  | "delete";
export type RefineInput = {
  customerId: string;
  folderId: string;
  title: string;
  sourceBody: string;
  files: FileRef[];
  instructions: string;
  guidelineId: string;
  model: string;
};
export type RefineView = {
  folderTreeProvider?: import("./library-adapter").LibraryAdapter;
  registrationResult?: RefineMutationResult;
  hqCustomerId?: string;
  uploadTarget?: { customerId: string; folderId: string | null };
  jobs: RefineJob[];
  selected?: RefineJob;
  total: number;
  loading?: boolean;
  error?: string;
  customers: { id: string; name: string }[];
  folders: { id: string; customerId: string; name: string }[];
  guidelines: Guideline[];
  models: AiModels;
  readiness?: { reason: string; usedUsd?: number; limitUsd?: number };
  canCreate: boolean;
};
export type RefineMutationResult = {
  revised?: { manuscriptId: string; revision: number; changed: boolean };
  manuscriptId?: string;
};
export type RefineActions = {
  enqueue?: import("./library-refine-upload").RefineUploadProvider["enqueue"];
  batch?: (input: RefineBatchInput) => Promise<RefineBatchResult>;
  showCreated?: (ids: string[]) => void;
  query?: (q: {
    q: string;
    folderId?: string;
    customerId?: string;
    status: string;
    offset: number;
    limit: number;
  }) => void;
  open?: (id: string) => void;
  back?: () => void;
  reload?: () => void;
  create?: (input: RefineInput) => Promise<void>;
  mutate?: (
    id: string,
    action: RefineAction,
    input: Record<string, unknown>,
  ) => Promise<void | RefineMutationResult>;
  uploadFile?: (customerId: string, file: File) => Promise<FileRef>;
  openManuscript?: (id: string) => void;
  openSettings?: () => void;
};
export const reviewFixture: ReviewNotice[] = [
  {
    code: "meta_lines",
    level: "warn",
    message: "핵심 키워드·독자 등 메타 줄 2개가 본문에 남아 있습니다.",
    line: 2,
  },
  {
    code: "table_text",
    level: "warn",
    message: "탭으로 만든 표는 게시할 때 칸이 뭉개집니다.",
    line: 8,
  },
  {
    code: "scale",
    level: "info",
    message: "링크 카드 13개 · 예상 입력 단계 85개",
    line: 14,
  },
  {
    code: "text_changed",
    level: "warn",
    message: "원문과 다른 문장 2개",
    data: {
      missing: ["예약은 전화로 안내합니다."],
      added: ["온라인 예약이 가능합니다."],
    },
  },
  {
    code: "ai_missed",
    level: "info",
    message: "AI 보고서가 놓친 항목 2개: 메타 줄·링크 카드 규모",
  },
];
export const guidelineFixture: Guideline[] = [
  ...(["refine", "generate"] as const),
].flatMap((kind) =>
  [3, 2, 1].map((version) => ({
    id: `${kind}-${version}`,
    kind,
    version,
    label: version === 3 ? "원문 보존과 게시 안정성" : "형식 표준 정리",
    body: `# ${kind === "refine" ? "정형화" : "생성"} 가이드라인 v${version}\n\n## 원문 보존\n원문 문장을 임의로 바꾸지 않습니다.\n\n## 형식\n사진과 영상은 첨부 파일 이름으로 연결합니다.\n${version === 3 ? "\n## 검수\n표·메타 줄·지시 문장을 별도로 보고합니다.\n" : "\n형식 충돌을 보고합니다.\n"}`,
    active: version === 3,
    author: "운영 관리자",
    createdAt: `2026-10-${18 + version} 10:00`,
    note: version === 3 ? "메타 줄과 링크 카드 누락 방지" : "최초 형식 지침",
    uses: version === 3 ? 12 : 28,
  })),
);
export function refineFixture(status: RefineStatus = "review"): RefineJob {
  const body =
    "# 진료 예약 안내\n\n핵심 키워드: 진료 예약\n독자: 첫 방문 고객\n\n예약은 전화로 안내합니다.\n\n요일\t시간\t안내\n월요일\t09:00\t예약\n\n여기에 대표 이미지를 삽입하세요.\n\n자세한 일정은 방문 전에 확인해 주세요.";
  const attempt: RefineAttempt = {
    seq: 3,
    sourceRevision: 2,
    guideline: { id: "refine-3", version: 3, label: "원문 보존과 게시 안정성" },
    model: "gpt-5-mini",
    provider: "openai",
    status: ["running", "queued", "failed", "cancelled"].includes(status)
      ? status
      : "done",
    decision: status === "registered" ? "등록됨" : undefined,
    registeredRevision: status === "registered" ? 2 : undefined,
    costUsd: 0.042,
    durationMs: 124000,
    startedAt: new Date(Date.now() - 72000).toISOString(),
    body: "# 진료 예약 안내\n\n온라인 예약이 가능합니다.\n\n## 방문 전 확인\n\n자세한 일정은 방문 전에 확인해 주세요.",
    report: {
      status: "확인 필요",
      hold: ["표의 열 구조를 원문과 대조해 주세요."],
      conflicts: [
        {
          line: 8,
          text: "요일 / 시간 / 안내",
          effect: "탭 표는 v1에서 지원하지 않습니다.",
        },
      ],
      removed: [
        { text: "여기에 대표 이미지를 삽입하세요.", movedTo: "직원 지시사항" },
      ],
      unusedFiles: ["sticker.png"],
      repeated: [{ text: "방문 전에 확인해 주세요.", count: 3 }],
      scale: {
        media: 4,
        videos: 1,
        linkCards: 13,
        headings: 6,
        paragraphs: 18,
        steps: 85,
      },
      registration: {
        keywords: ["진료 예약", "방문 안내"],
        schedule: "2026-10-24 09:00",
        memo: "예약 안내 게시",
      },
    },
    notices: reviewFixture,
    parse: { errors: [], warnings: ["사진 설명은 현재 게시되지 않습니다."] },
    context: [
      "이전 반려: 출처 링크 카드 13개를 다시 확인해 주세요.",
      "이전 보류: 표의 열 구조",
      "이전 자동 검토: 메타 줄·게시 규모",
    ],
  };
  return {
    id: "REF-12",
    title: "진료 예약 안내",
    customerId: "onyou",
    customer: "온유의원",
    status,
    author: "운영 담당자",
    createdAt: "2026-10-22 10:24",
    folderId: "folder-oct",
    sources: [
      { revision: 1, body: body.replace("전화로", "방문 후"), files: [] },
      {
        revision: 2,
        body,
        files: [
          { fileId: "fixture-photo", name: "clinic.jpg" },
          { fileId: "fixture-sticker", name: "sticker.png" },
        ],
      },
    ],
    attempts: [
      {
        ...attempt,
        seq: 1,
        sourceRevision: 1,
        guideline: { id: "refine-2", version: 2, label: "형식 표준 정리" },
        registeredRevision: undefined,
        decision: "반려",
        decisionNote: "출처 13개 카드",
        body: "# 이전 결과\n\n방문 후 예약해 주세요.",
      },
      {
        ...attempt,
        seq: 2,
        registeredRevision: undefined,
        decision: "반려",
        decisionNote: "원문 문장 보존",
        body: "# 이전 결과\n\n예약 안내입니다.",
      },
      attempt,
    ],
    manuscriptId: status === "registered" ? "LIB-REF12" : undefined,
    linkedManuscript:
      status === "registered"
        ? { title: "진료 예약 안내", revision: 2 }
        : undefined,
    allowedActions:
      status === "registered"
        ? ["rerun", "source"]
        : status === "running" || status === "queued"
          ? ["cancel", "delete"]
          : status === "failed"
            ? ["rerun", "source", "delete"]
            : status === "cancelled"
              ? ["rerun", "source", "delete"]
              : ["register", "reject", "rerun", "source", "delete"],
  };
}
